import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import DashboardLayout from './layouts/DashboardLayout';
import DashboardPage from './pages/DashboardPage';
import AziendePage from './pages/AziendePage';
import CompanyDetailPage from './pages/CompanyDetailPage';
import LavoratoriPage from './pages/LavoratoriPage';
import FormazionePage from './pages/FormazionePage';
import TitoliDaVerificarePage from './pages/TitoliDaVerificarePage';
import SorveglianzaPage from './pages/SorveglianzaPage';
import DpiPage from './pages/DpiPage';
import HaccpPage from './pages/HaccpPage';
import SicurezzaPage from './pages/SicurezzaPage';
import CalendarioPage from './pages/CalendarioPage';
import ComunicazioniPage from './pages/ComunicazioniPage';
import ReportPage from './pages/ReportPage';
import BulkOperationsPage from './pages/BulkOperationsPage';
import UsersPage from './pages/UsersPage';
import AuditPage from './pages/AuditPage';
import MailingPage from './pages/MailingPage';
import MigrationPage from './pages/MigrationPage';
import ReconciliationPage from './pages/ReconciliationPage';
import DuplicatesPage from './pages/DuplicatesPage';
import DuplicatesPrivatiPage from './pages/DuplicatesPrivatiPage';
import PrivatiFileMergePage from './pages/PrivatiFileMergePage';
import RolesImportPage from './pages/RolesImportPage';
import FormazioneAlignImportPage from './pages/FormazioneAlignImportPage';
import VisiteAlignImportPage from './pages/VisiteAlignImportPage';
import ContrattiServiziImportPage from './pages/ContrattiServiziImportPage';
import NormalizeNamesPage from './pages/NormalizeNamesPage';
import PiscinaPage from './pages/PiscinaPage';
import RegistriHaccpPage from './pages/RegistriHaccpPage';
import PresenzeImportPage from './pages/PresenzeImportPage';
import ImportAziendePage from './pages/ImportAziendePage';
import SopralluoghiPage from './pages/SopralluoghiPage';
import RegistriPage from './pages/RegistriPage';
import RegistroDetailPage from './pages/RegistroDetailPage';
import WhatsAppButton from './components/WhatsAppButton';
import WorkerDetailPage from './pages/WorkerDetailPage';
import MenuGuard from './components/MenuGuard';
import CookieBanner from './components/CookieBanner';
import { Toaster } from './components/ui/sonner';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const RegistriHaccpWithUser = () => {
  const { user } = useAuth();
  return <RegistriHaccpPage user={user} />;
};

const RedirectIfAuth = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/panoramica" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<RedirectIfAuth><LoginPage /></RedirectIfAuth>} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/panoramica" replace />} />
              <Route path="panoramica" element={<DashboardPage />} />
              <Route path="aziende" element={<MenuGuard><AziendePage /></MenuGuard>} />
              <Route path="aziende/importa" element={<MenuGuard><ImportAziendePage /></MenuGuard>} />
              <Route path="aziende/:id" element={<MenuGuard><CompanyDetailPage /></MenuGuard>} />
              <Route path="lavoratori" element={<MenuGuard><LavoratoriPage /></MenuGuard>} />
              <Route path="lavoratori/:id" element={<MenuGuard><WorkerDetailPage /></MenuGuard>} />
              <Route path="formazione" element={<MenuGuard><FormazionePage /></MenuGuard>} />
              <Route path="formazione/da-fare" element={<Navigate to="/formazione?status=da_fare" replace />} />
              <Route path="formazione/titoli-da-verificare" element={<MenuGuard><TitoliDaVerificarePage /></MenuGuard>} />
              <Route path="sorveglianza" element={<MenuGuard><SorveglianzaPage /></MenuGuard>} />
              <Route path="dpi" element={<MenuGuard><DpiPage /></MenuGuard>} />
              <Route path="haccp" element={<MenuGuard><HaccpPage /></MenuGuard>} />
              <Route path="registri-haccp" element={<MenuGuard><RegistriHaccpWithUser /></MenuGuard>} />
              <Route path="sopralluoghi" element={<MenuGuard><SopralluoghiPage /></MenuGuard>} />
              <Route path="registri" element={<MenuGuard><RegistriPage /></MenuGuard>} />
              <Route path="registri/:code" element={<MenuGuard><RegistroDetailPage /></MenuGuard>} />
              <Route path="sicurezza" element={<MenuGuard><SicurezzaPage /></MenuGuard>} />
              <Route path="calendario" element={<MenuGuard><CalendarioPage /></MenuGuard>} />
              <Route path="comunicazioni" element={<MenuGuard><ComunicazioniPage /></MenuGuard>} />
              <Route path="report" element={<MenuGuard><ReportPage /></MenuGuard>} />
              <Route path="reportistica" element={<Navigate to="/report" replace />} />
              <Route path="operazioni-massive" element={<MenuGuard><BulkOperationsPage /></MenuGuard>} />
              <Route path="utenti" element={<MenuGuard><UsersPage /></MenuGuard>} />
              <Route path="audit" element={<MenuGuard><AuditPage /></MenuGuard>} />
              <Route path="mailing" element={<MenuGuard><MailingPage /></MenuGuard>} />
              <Route path="migrazione" element={<MenuGuard><MigrationPage /></MenuGuard>} />
              <Route path="riconciliazione" element={<MenuGuard><ReconciliationPage /></MenuGuard>} />
              <Route path="riconciliazione/duplicati" element={<MenuGuard><DuplicatesPage /></MenuGuard>} />
              <Route path="riconciliazione/duplicati-privati" element={<MenuGuard><DuplicatesPrivatiPage /></MenuGuard>} />
              <Route path="riconciliazione/merge-da-file" element={<MenuGuard><PrivatiFileMergePage /></MenuGuard>} />
              <Route path="riconciliazione/ruoli-import" element={<MenuGuard><RolesImportPage /></MenuGuard>} />
              <Route path="riconciliazione/formazione-align" element={<MenuGuard><FormazioneAlignImportPage /></MenuGuard>} />
              <Route path="riconciliazione/visite-align" element={<MenuGuard><VisiteAlignImportPage /></MenuGuard>} />
              <Route path="riconciliazione/presenze-import" element={<MenuGuard><PresenzeImportPage /></MenuGuard>} />
              <Route path="riconciliazione/contratti-servizi" element={<MenuGuard><ContrattiServiziImportPage /></MenuGuard>} />
              <Route path="riconciliazione/normalizza-nomi" element={<MenuGuard><NormalizeNamesPage /></MenuGuard>} />
              <Route path="piscina" element={<MenuGuard><PiscinaPage /></MenuGuard>} />
            </Route>
            <Route path="*" element={<Navigate to="/panoramica" replace />} />
          </Routes>
          <Toaster position="top-right" />
          <WhatsAppButton />
          <CookieBanner />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
